import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef, useCallback } from "react";
import { useToastStore } from "&/toast";
import { CoinPreOrderResponse } from "~/types/payment";

// iOS 웹뷰에서 Xcode 콘솔에 로그를 출력하기 위한 헬퍼 함수
// const nativeLog = (message: string, data?: any) => {
//   const isWebApp = /MT_I|MT_A/i.test(navigator.userAgent);
//
//   if (isWebApp && (window as any).webkit?.messageHandlers?.mung) {
//     const logMessage = data
//       ? `${message}\n${typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data)}`
//       : message;
//
//     try {
//       (window as any).webkit.messageHandlers.mung.postMessage(["consoleLog", logMessage]);
//     } catch (error) {
//       console.error("nativeLog postMessage 실패:", error);
//     }
//   }
// };

const nativeError = (message: string, error?: any) => {
  const isWebApp = /MT_I|MT_A/i.test(navigator.userAgent);

  if (isWebApp && (window as any).webkit?.messageHandlers?.mung) {
    // 에러 정보를 상세하게 포맷팅
    let errorMessage = `❌ ${message}`;
    if (error) {
      errorMessage += `\n에러 타입: ${typeof error}`;
      errorMessage += `\n에러 이름: ${(error as Error)?.name || "N/A"}`;
      errorMessage += `\n에러 메시지: ${(error as Error)?.message || String(error)}`;
      if ((error as Error)?.stack) {
        errorMessage += `\n에러 스택:\n${(error as Error).stack}`;
      }
      try {
        errorMessage += `\n에러 객체:\n${JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}`;
      } catch {
        errorMessage += `\n에러 객체: [직렬화 불가]`;
      }
    }
    (window as any).webkit.messageHandlers.mung.postMessage([
      "consoleLog",
      errorMessage,
    ]);
  }

  // 일반 console.error도 함께 출력
  if (error !== undefined) {
    console.error(message, error);
  } else {
    console.error(message);
  }
};

export const Route = createFileRoute("/customer/layout/payment/")({
  component: Payment,
});

function Payment() {
  const { add } = useToastStore();
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState<any>(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const paymentMethodWidgetRef = useRef<any>(null);
  const agreementWidgetRef = useRef<any>(null);
  const [payRes, setPayRes] = useState<CoinPreOrderResponse["data"] | null>(
    null
  );
  // 🔥 중복 결제 방지: 결제 진행 중 상태
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const paymentTimeoutRef = useRef<number | null>(null);

  // 토스페이먼츠 v2 SDK 초기화 (일반 결제)
  // MCP 문서 기반: https://docs.tosspayments.com/sdk/v2/js#tosspaymentswidgets
  const initializeTossPayments = useCallback(async () => {
    try {
      // payRes에서 customerKey를 가져오거나, 없으면 생성
      const customerKey =
        payRes?.customerKey ||
        `CUSTOMER_${crypto.randomUUID().replace(/-/g, "")}`;

      // Client Key는 환경 변수 또는 payRes에서 가져오기
      const clientKey =
        payRes?.widgetClientKey ||
        import.meta.env.VITE_TOSS_CLIENT_KEY ||
        "test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm";

      // 이미 로드된 스크립트가 있는지 확인
      if (
        document.querySelector(
          'script[src="https://js.tosspayments.com/v2/standard"]'
        )
      ) {
        if ((window as any).TossPayments) {
          const tosspayments = (window as any).TossPayments(clientKey);

          // 일반 결제: widgets() 메서드로 결제위젯 인스턴스 생성
          // MCP 문서: brandpay 옵션 없이 customerKey만 사용
          const widgetsInstance = tosspayments.widgets({
            customerKey,
          });
          setWidgets(widgetsInstance);
        }
        return;
      }

      // v2 SDK 로드
      const script = document.createElement("script");
      script.src = "https://js.tosspayments.com/v2/standard";
      script.onload = async () => {
        if ((window as any).TossPayments) {
          const tosspayments = (window as any).TossPayments(clientKey);

          // 일반 결제: widgets() 메서드로 결제위젯 인스턴스 생성
          // MCP 문서: brandpay 옵션 없이 customerKey만 사용
          const widgetsInstance = tosspayments.widgets({
            customerKey,
          });
          setWidgets(widgetsInstance);
        }
      };
      document.head.appendChild(script);
    } catch (error) {
      console.error("토스페이먼츠 v2 초기화 실패:", error);
      add("결제 시스템을 불러오는데 실패했습니다.");
    }
  }, [add, payRes]);

  // 토스페이먼츠 v2 위젯 로드
  // payRes가 있을 때만 초기화 (customerKey를 payRes에서 가져오기 위해)
  useEffect(() => {
    if (payRes) {
      initializeTossPayments();
    }
  }, [initializeTossPayments, payRes]);

  // 위젯 렌더링 (MCP 문서 기반)
  const renderWidget = useCallback(async () => {
    if (!widgets || !payRes) return;

    try {
      // 기존 위젯이 있다면 제거
      if (paymentMethodWidgetRef.current) {
        await paymentMethodWidgetRef.current.destroy();
        paymentMethodWidgetRef.current = null;
      }
      if (agreementWidgetRef.current) {
        await agreementWidgetRef.current.destroy();
        agreementWidgetRef.current = null;
      }

      // MCP 문서 기반: 주문의 결제 금액 설정 (반드시 먼저 설정)
      // @docs https://docs.tosspayments.com/sdk/v2/js#widgetssetamount
      await widgets.setAmount({
        currency: "KRW",
        value: payRes.costAmount,
      });

      // MCP 문서 기반: Promise.all로 동시 렌더링
      // @docs https://docs.tosspayments.com/sdk/v2/js#widgetsrenderpaymentmethods
      // @docs https://docs.tosspayments.com/sdk/v2/js#widgetsrenderagreement
      const [paymentMethodWidget, agreementWidget] = await Promise.all([
        // 결제 UI 렌더링
        widgets.renderPaymentMethods({
          selector: "#payment-widget",
          variantKey: "DEFAULT",
        }),
        // 이용약관 UI 렌더링
        widgets.renderAgreement({
          selector: "#agreement-widget",
          variantKey: "AGREEMENT",
        }),
      ]);

      // 위젯 인스턴스를 ref에 저장
      paymentMethodWidgetRef.current = paymentMethodWidget;
      agreementWidgetRef.current = agreementWidget;

      // 일반 결제: paymentMethodSelect 이벤트 리스너
      paymentMethodWidget.on(
        "paymentMethodSelect",
        (selectedPaymentMethod: any) => {
          // 브랜드페이 선택 시 경고 및 차단
          if (
            selectedPaymentMethod.method === "BRANDPAY" ||
            selectedPaymentMethod.code === "BRANDPAY"
          ) {
            console.warn(
              "브랜드페이는 현재 사용할 수 없습니다. 다른 결제수단을 선택해주세요."
            );
            add(
              "브랜드페이는 현재 사용할 수 없습니다. 다른 결제수단을 선택해주세요."
            );
            setIsWidgetReady(false);
            return;
          }

          // 일반 결제는 바로 준비 완료
          setIsWidgetReady(true);
        }
      );

      // 초기 상태 설정 (일반 결제는 바로 준비 완료)
      setIsWidgetReady(true);
    } catch (error) {
      console.error("위젯 렌더링 실패:", error);
      add("결제 위젯을 불러오는데 실패했습니다.");
    }
  }, [widgets, payRes, add]);

  // 위젯 렌더링 실행
  useEffect(() => {
    if (!widgets || !payRes) return;

    // 위젯이 이미 렌더링되었는지 확인
    if (paymentMethodWidgetRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      renderWidget();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [widgets, payRes?.orderId, renderWidget]);

  // 결제 요청 처리 (MCP 문서 기반)
  const handlePayment = useCallback(async () => {
    // 🔥 중복 결제 방지: 이미 진행 중이면 무시
    if (isPaymentProcessing) {
      add("결제가 이미 진행 중입니다. 잠시만 기다려주세요.");
      return;
    }

    if (!widgets || !payRes || !isWidgetReady) {
      add("결제 시스템이 준비되지 않았습니다.");
      return;
    }

    // 🔥 결제 진행 중 상태로 변경
    setIsPaymentProcessing(true);

    // 🔥 결제 정보 세션 스토리지에 저장 (취소 시 복구용)
    if (payRes?.orderId) {
      try {
        const paymentData = {
          orderId: payRes.orderId,
          amount: payRes.costAmount,
          customerName: payRes.customerName,
          customerEmail: payRes.customerEmail,
          companySeq: (payRes as any)?.companySeq,
          restoreState: (payRes as any)?.restoreState,
          selectedPlans: (payRes as any)?.selectedPlans,
          timestamp: Date.now(),
        };
        sessionStorage.setItem("currentPaymentOrderId", payRes.orderId);
        sessionStorage.setItem(
          "currentPaymentData",
          JSON.stringify(paymentData)
        );
      } catch (error) {
        nativeError("결제 정보 세션 스토리지 저장 실패", error);
      }
    }

    // 🔥 안전장치: 30초 후 자동 해제 (타임아웃)
    if (paymentTimeoutRef.current) {
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current as number);
      }
      paymentTimeoutRef.current = window.setTimeout(() => {
        setIsPaymentProcessing(false);
        paymentTimeoutRef.current = null;
      }, 30000);
    }

    try {
      // 웹앱 환경 감지 (MT_I: iOS, MT_A: Android)
      // const userAgent = navigator.userAgent;
      // const isWebApp = /MT_I|MT_A/i.test(userAgent);
      // const isMobile =
      //   /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      //     userAgent
      //   );

      // 주문명 설정
      const orderName = "이용권 구매";

      // companySeq와 restoreState가 있으면 successUrl에 포함
      const companySeq = (payRes as any)?.companySeq;
      const restoreState = (payRes as any)?.restoreState;
      const successUrlParams = new URLSearchParams({
        orderId: payRes.orderId,
        amount: String(payRes.costAmount),
      });
      if (companySeq) {
        successUrlParams.set("companySeq", String(companySeq));
      }
      if (restoreState) {
        successUrlParams.set("restoreState", restoreState);
      }

      // MCP 문서 기반: requestPayment 호출
      // @docs https://docs.tosspayments.com/sdk/v2/js#widgetsrequestpayment
      // customerMobilePhone은 전화번호 형식이어야 하므로, 없거나 형식이 맞지 않으면 전달하지 않음
      // 웹앱/모바일 환경에서는 windowTarget을 'self'로 명시적으로 설정해야 함

      // VITE_APP_URL이 없으면 현재 origin 사용
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

      const webSuccessUrl = `${baseUrl}/customer/layout/payment/success?${successUrlParams.toString()}`;
      const webFailUrl = `${baseUrl}/customer/layout/payment/fail?orderId=${payRes.orderId}`;

      const paymentRequestParams: any = {
        orderId: payRes.orderId,
        orderName: orderName,
        successUrl: webSuccessUrl, // 웹 URL 사용
        failUrl: webFailUrl, // 웹 URL 사용
        customerEmail: payRes.customerEmail,
        customerName: payRes.customerName,
      };

      // windowTarget 제거: iOS WebView에서 작동하지 않으며, 모바일 WebView는 이미 전체 페이지 컨텍스트이므로 불필요
      // 카드사 앱 전환은 외부 앱 이동이므로 windowTarget과 무관

      // customerMobilePhone이 있고 전화번호 형식인 경우에만 추가
      const customerMobilePhone = (payRes as any).customerMobilePhone;
      if (customerMobilePhone && /^[0-9-]+$/.test(customerMobilePhone)) {
        paymentRequestParams.customerMobilePhone = customerMobilePhone;
      }

      try {
        await widgets.requestPayment(paymentRequestParams);

        // 🔥 결제 요청 성공 시 상태 해제 (외부 앱으로 이동하므로)
        setIsPaymentProcessing(false);
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }
      } catch (requestError) {
        nativeError("=== requestPayment 내부 에러 ===", requestError);
        throw requestError; // 상위 catch로 전달
      }
    } catch (error) {
      nativeError("결제 요청 실패", error);

      // 🔥 에러 발생 시 결제 진행 중 상태 해제
      setIsPaymentProcessing(false);
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }

      add("결제 요청에 실패했습니다.");
    }
  }, [
    widgets,
    payRes,
    isWidgetReady,
    add,
    isPaymentProcessing,
    payRes?.orderId,
  ]);

  // 결제 취소 처리
  const handleCancel = useCallback(() => {
    // 위젯 정리
    if (paymentMethodWidgetRef.current) {
      paymentMethodWidgetRef.current.destroy().catch(console.error);
    }
    if (agreementWidgetRef.current) {
      agreementWidgetRef.current.destroy().catch(console.error);
    }

    // 이용권 구매 페이지로 돌아가기 (companySeq 및 선택된 이용권 정보 포함)
    const companySeq = (payRes as any)?.companySeq;
    const selectedPlans = (payRes as any)?.selectedPlans;

    const searchParams: Record<string, string> = {};
    if (companySeq) {
      searchParams.companySeq = String(companySeq);
    }
    if (
      selectedPlans &&
      Array.isArray(selectedPlans) &&
      selectedPlans.length > 0
    ) {
      // 선택된 이용권 정보를 JSON으로 인코딩하여 전달
      searchParams.selectedPlans = encodeURIComponent(
        JSON.stringify(selectedPlans)
      );
    }

    navigate({
      to: "/customer/layout/subscription-list",
      search: Object.keys(searchParams).length > 0 ? searchParams : undefined,
    });
  }, [navigate, payRes]);

  // URL 파라미터에서 결제 정보 받기
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentData = urlParams.get("paymentData");

    if (paymentData) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(paymentData));
        setPayRes(parsedData);
      } catch (error) {
        console.error("결제 데이터 파싱 실패:", error);
        add("결제 정보를 불러오는데 실패했습니다.");
      }
    }
  }, [add]);

  // 🔥 취소/완료 플래그 확인 및 상태 해제 (결제 취소/완료 후 복귀 시)
  useEffect(() => {
    // 플래그 확인 함수
    const checkPaymentFlags = () => {
      // 취소 플래그 확인
      const isCanceled = sessionStorage.getItem("paymentCanceled");
      if (isCanceled === "true") {
        setIsPaymentProcessing(false);
        sessionStorage.removeItem("paymentCanceled");
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }
        return true;
      }

      // 결제 완료 플래그 확인
      const isCompleted = sessionStorage.getItem("paymentCompleted");
      if (isCompleted === "true") {
        setIsPaymentProcessing(false);
        sessionStorage.removeItem("paymentCompleted");
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }
        return true;
      }
      return false;
    };

    // 초기 마운트 시 플래그 확인
    checkPaymentFlags();

    // 🔥 페이지 가시성 감지 (앱 복귀 시 자동 확인)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // 앱이 포그라운드로 돌아왔을 때 플래그 확인
        checkPaymentFlags();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // 🔥 컴포넌트 언마운트 시 타임아웃 정리
  useEffect(() => {
    return () => {
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }
    };
  }, []);

  if (!payRes) {
    return (
      <div className="flex justify-center items-center flex-auto">
        <div className="text-center">
          <div className="mb-2 text-lg font-semibold">
            결제 정보를 불러오는 중...
          </div>
          <div className="text-gray-600">잠시만 기다려주세요.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-auto bg-gray-50">
      <div className="px-4 pt-8 pb-8 mx-auto max-w-md">
        {/* 결제 정보 */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
          <h1 className="mb-4 text-xl font-bold text-center">결제 정보</h1>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">주문번호</span>
              <span className="font-medium">{payRes.orderId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">결제금액</span>
              <span className="font-medium text-orange-400">
                {payRes.costAmount.toLocaleString()}원
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">구매수량</span>
              <span className="font-medium">{payRes.purchaseQuantity}개</span>
            </div>
          </div>
        </div>

        {/* 토스페이먼츠 v2 위젯 렌더링 영역 */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">결제 수단 선택</h2>
          <div id="payment-widget" className="mb-6"></div>
        </div>

        <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">이용약관</h2>
          <div id="agreement-widget" className="mb-6"></div>
        </div>

        {/* 결제 버튼 */}
        <div className="p-6 bg-white rounded-lg shadow-sm">
          <button
            className={`px-6 py-4 mb-3 w-full text-lg font-medium rounded-lg transition-colors ${
              isWidgetReady && !isPaymentProcessing
                ? "text-white bg-orange-400 hover:bg-orange-500"
                : "text-gray-400 bg-gray-200 cursor-not-allowed"
            }`}
            onClick={handlePayment}
            disabled={!isWidgetReady || isPaymentProcessing}
          >
            {isPaymentProcessing ? (
              <span className="flex justify-center items-center">
                <span className="inline-block mr-2 w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></span>
                결제 처리 중...
              </span>
            ) : isWidgetReady ? (
              "결제하기"
            ) : (
              "결제 시스템 준비 중..."
            )}
          </button>
          <button
            className={`px-6 py-3 w-full text-base font-medium rounded-lg transition-colors ${
              isPaymentProcessing
                ? "text-gray-400 bg-gray-200 cursor-not-allowed"
                : "text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300"
            }`}
            onClick={handleCancel}
            disabled={isPaymentProcessing}
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// 토스페이먼츠 v2 타입 정의 (일반 결제)
declare global {
  interface Window {
    TossPayments: (clientKey: string) => {
      widgets: (options: { customerKey: string }) => {
        setAmount: (options: {
          value: number;
          currency: string;
        }) => Promise<void>;
        renderPaymentMethods: (options: {
          selector: string;
          variantKey?: string;
        }) => Promise<{
          on: (event: string, callback: (data: any) => void) => void;
          destroy: () => Promise<void>;
        }>;
        renderAgreement: (options: {
          selector: string;
          variantKey?: string;
        }) => Promise<{
          destroy: () => Promise<void>;
        }>;
        requestPayment: (paymentData: any) => Promise<any>;
      };
    };
  }
}
